import { OverflowList } from '@theme/critical';
import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, PaintFilterChangedEvent, PaintFilterClearedEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/** Factor for converting longest option text length to em-based column width. */
const VARIANT_CH_EM_FACTOR = 0.65;

/** @type {RegExp} */
const VOLUME_OPTION_NAME_PATTERN = /inhoud|size|maat|grootte|afmeting|volume|liter|lengte/i;

/**
 * SHO-10261: lokale helper i.p.v. paint-calculator-core — behang-PDP heeft
 * volume-optienamen (afmeting/lengte) maar mag de paint-bundle niet laden.
 *
 * @param {string | null | undefined} text
 * @returns {number | null}
 */
function parseLitersFromText(text) {
  if (!text || typeof text !== 'string') return null;

  const normalized = text.trim().toLowerCase().replace(',', '.');

  const literMatch = normalized.match(/(\d+(?:\.\d+)?)\s*(?:l|liter|litre)\b/);
  if (literMatch) {
    const liters = parseFloat(literMatch[1]);
    return Number.isFinite(liters) && liters > 0 ? liters : null;
  }

  const mlMatch = normalized.match(/(\d+(?:\.\d+)?)\s*(?:ml|milliliter|milliliters)\b/);
  if (mlMatch) {
    const ml = parseFloat(mlMatch[1]);
    return Number.isFinite(ml) && ml > 0 ? ml / 1000 : null;
  }

  return null;
}

/**
 * @param {string | null | undefined} optionName
 * @returns {boolean}
 */
function isVolumeOptionName(optionName) {
  return VOLUME_OPTION_NAME_PATTERN.test(optionName || '');
}

/**
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @returns {number}
 */
function compareVolumeOptionValues(a, b) {
  const aLiters = parseLitersFromText(a);
  const bLiters = parseLitersFromText(b);

  if (aLiters !== null && bLiters !== null && aLiters !== bLiters) {
    return aLiters - bLiters;
  }

  if (aLiters !== null && bLiters === null) return -1;
  if (aLiters === null && bLiters !== null) return 1;

  return (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * @param {string | null | undefined} url
 * @returns {string}
 */
function normalizeProductPath(url) {
  if (!url) return '';

  try {
    return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
  } catch {
    return url.split('?')[0].replace(/\/$/, '');
  }
}

/**
 * @param {string | null | undefined} url
 * @returns {string}
 */
function extractProductHandle(url) {
  const path = normalizeProductPath(url);
  const marker = '/products/';
  const index = path.lastIndexOf(marker);
  if (index === -1) return '';

  return path.slice(index + marker.length).split('/')[0] || '';
}

/** Selector voor de primaire `media-gallery` op de product-PDP. */
const PDP_PRIMARY_GALLERY_SELECTOR = 'main .product-information__media media-gallery';

/**
 * Combined listings: na een fetch wordt `main` gemorph. `morph` verplaatst knopen uit het
 * parse-document naar het live document; daarna is `parsedHtml.querySelector(media-gallery)`
 * leeg of incompleet. Clone de gallery daarom vóór `updateMain` en geef die door als
 * `meta.galleryClone`.
 *
 * @param {Document} parsedHtml
 * @param {{ galleryClone?: HTMLElement | null }} [meta]
 */
function replaceProductMediaGalleryFromFetchedHtml(parsedHtml, meta = {}) {
  const selector = PDP_PRIMARY_GALLERY_SELECTOR;
  const fresh =
    meta.galleryClone instanceof HTMLElement
      ? meta.galleryClone
      : parsedHtml.querySelector(selector);
  const live = document.querySelector(selector);

  if (!(fresh instanceof HTMLElement) || !(live instanceof HTMLElement)) return;

  live.replaceWith(document.importNode(fresh, true));
}

/**
 * Combined listings: module-scripts die de morph uit het parse-document overneemt zijn per
 * HTML-spec unexecutable (DOMParser). Een blok dat pas ná een switch verschijnt (bv.
 * bezorg-retour, variant-additional-products) laadt zijn module daardoor nooit — het custom
 * element blijft permanent un-upgraded. Importeer daarom na elke main-morph alle
 * module-scripts in `main`; de browser-modulecache maakt al geladen imports gratis.
 *
 * @param {Element} main
 */
function importMorphedModuleScripts(main) {
  for (const script of main.querySelectorAll('script[type="module"][src]')) {
    const src = script.getAttribute('src');
    if (!src) continue;

    try {
      import(new URL(src, document.baseURI).href).catch((error) => {
        console.error('[variant-picker] module-script laden na morph mislukt:', src, error);
      });
    } catch (error) {
      console.error('[variant-picker] ongeldige module-src na morph:', src, error);
    }
  }
}

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl
  
  /** @type {boolean} */
  #isAutoSelecting = false;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #connectionAbortController;

  /** @type {string | null} */
  #currentPaintFilter = null;

  /** @type {boolean} */
  #allowAutoSelection = true;

  /** @type {string | null} */
  #pendingCombinedListingOptionValue = null;

  connectedCallback() {
    super.connectedCallback();

    // A main morph can temporarily disconnect and reconnect the same picker instance.
    // Always replace the previous listener set so one user change triggers one request.
    this.#connectionAbortController?.abort();
    this.#connectionAbortController = new AbortController();
    const { signal } = this.#connectionAbortController;

    this.addEventListener('change', this.variantChanged.bind(this), { signal });
    this.addEventListener('change', this.#handleVariantChangeForFiltering.bind(this), { signal });
    document.addEventListener('paint-filter:changed', this.#handlePaintFilterChanged.bind(this), { signal });
    document.addEventListener('paint-filter:cleared', this.#handlePaintFilterCleared.bind(this), { signal });
    document.addEventListener('paint-filter:user-interacted', this.#handlePaintFilterInteraction.bind(this), { signal });
    this.#sortVolumeOptions();
    this.#syncCombinedListingOptionSelection();

    if (!this.classList.contains('variant-picker--has-paint-filter')) {
      this.#updateAllFieldsetColumnWidths();
    }

    // Delay initial sync to ensure paint filter is ready
    setTimeout(() => {
      if (signal.aborted) return;
      this.#syncInitialPaintFilterState();
    }, 50);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connectionAbortController?.abort();
  }

  /**
   * Sort volume option values (e.g. inhoud) by size instead of Shopify's default order.
   * @private
   */
  #sortVolumeOptions() {
    const optionGroups = this.querySelectorAll('[data-option-name]');

    for (const group of optionGroups) {
      if (!(group instanceof HTMLElement)) continue;

      const optionName = group.dataset.optionName;
      if (!isVolumeOptionName(optionName)) continue;

      if (group.matches('fieldset.variant-option--buttons, fieldset.variant-option--swatches')) {
        const labels = Array.from(group.querySelectorAll(':scope > .variant-option__button-label'));
        if (labels.length < 2) continue;

        const sortedLabels = [...labels].sort((labelA, labelB) => {
          const valueA = labelA.querySelector('input[type="radio"]')?.value || labelA.textContent?.trim();
          const valueB = labelB.querySelector('input[type="radio"]')?.value || labelB.textContent?.trim();
          return compareVolumeOptionValues(valueA, valueB);
        });

        sortedLabels.forEach((label) => group.appendChild(label));
        continue;
      }

      const select = group.querySelector('select.variant-option__select');
      if (!(select instanceof HTMLSelectElement)) continue;

      const options = Array.from(select.querySelectorAll('option'));
      if (options.length < 2) continue;

      const sortedOptions = [...options].sort((optionA, optionB) => {
        return compareVolumeOptionValues(optionA.value, optionB.value);
      });

      sortedOptions.forEach((option) => select.appendChild(option));
    }
  }

  /**
   * Handle paint filter user interaction event
   * Disables auto-selection for future variant changes
   * Note: We don't convert disabled to hidden here anymore - that happens in #filterStepByApplicationOnly
   * @private
   */
  #handlePaintFilterInteraction(event) {
    this.#allowAutoSelection = false;
    // The actual filtering and conversion from disabled to hidden will happen
    // when #filterStepByApplicationOnly is called after the paint filter change event
  }

  /**
   * Sync initial paint filter state
   */
  #syncInitialPaintFilterState() {
    if (!this.classList.contains('variant-picker--has-paint-filter')) {
      this.#updateAllFieldsetColumnWidths();
      return;
    }
    
    // Find the paint filter component
    const paintFilter = document.querySelector('paint-application-filter');
    if (!paintFilter) {
      this.#showAllVariants();
      return;
    }

    // Find the checked radio button, or fall back to the first option
    let checkedButton = paintFilter.querySelector('input[type="radio"]:checked');
    if (!(checkedButton instanceof HTMLInputElement)) {
      checkedButton = paintFilter.querySelector('input[type="radio"]');
      if (checkedButton instanceof HTMLInputElement) {
        checkedButton.checked = true;
      }
    }

    if (checkedButton instanceof HTMLInputElement) {
      const application = checkedButton.dataset.application;
      
      // Set internal state
      this.#currentPaintFilter = application || null;
      
      // Apply the filter
      if (application) {
        this.#filterVariantsByApplication(application);
        return;
      }
    }

    this.#showAllVariants();
  }

  /**
   * Updates --variant-ch on equal-width button fieldsets (visible labels).
   * Kolommen komen uit CSS auto-fit; geen --variant-columns (dat was 2→3 CLS).
   * @param {HTMLElement | null | undefined} fieldset
   * @private
   */
  #updateFieldsetColumnWidth(fieldset) {
    if (!(fieldset instanceof HTMLElement)) return;
    if (!fieldset.classList.contains('variant-option--equal-width-buttons')) return;

    const visibleLabels = fieldset.querySelectorAll(
      '.variant-option__button-label:not(.variant-option--filtered-out):not(.variant-option--hidden-by-liquid)'
    );

    let longest = 0;
    visibleLabels.forEach((label) => {
      const text =
        label.querySelector('.variant-option__button-label__text')?.textContent?.trim() ||
        label.querySelector('input[type="radio"]')?.value?.trim() ||
        '';
      longest = Math.max(longest, text.length);
    });

    fieldset.style.removeProperty('--variant-columns');

    if (longest > 0) {
      fieldset.style.setProperty('--variant-ch', `${longest * VARIANT_CH_EM_FACTOR}em`);
    } else {
      fieldset.style.removeProperty('--variant-ch');
    }
  }

  /**
   * Updates column widths for all equal-width button fieldsets (variant steps + paint filter).
   * @private
   */
  #updateAllFieldsetColumnWidths() {
    const fieldsets = new Set([
      ...this.querySelectorAll('fieldset.variant-option--equal-width-buttons'),
      ...document.querySelectorAll('paint-application-filter fieldset.variant-option--equal-width-buttons'),
    ]);

    const update = () => {
      fieldsets.forEach((fieldset) => {
        this.#updateFieldsetColumnWidth(/** @type {HTMLElement} */ (fieldset));
      });
    };

    update();
    requestAnimationFrame(update);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) {
      return;
    }

    const fieldset = event.target.closest('fieldset');

    this.updateSelectedOption(event.target);
    const fallbackVariantId = this.#selectValidVariantCombination(selectedOption);

    if (fieldset?.classList.contains('variant-option--swatches')) {
      this.#updateSwatchValueDisplay(fieldset, selectedOption);
    }

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
    
    // After user clicks on a variant, disable auto-selection and switch to hiding disabled options
    if (this.#allowAutoSelection) {
      this.#allowAutoSelection = false;
      
      // Immediately hide disabled options in all variant steps
      if (this.#currentPaintFilter) {
        this.#filterVariantsByApplication(this.#currentPaintFilter);
      }
    }
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    if (loadsNewProduct && selectedOption instanceof HTMLInputElement) {
      this.#pendingCombinedListingOptionValue = selectedOption.value;
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);
    
    // Reapply paint filter after variant change
    // Wait for paint filter to be reapplied and disabled states to be updated before auto-selecting
    this.#reapplyPaintFilter().then(() => {
      // Update visual state for steps after afwerking
      this.#updateStepsAfterAfwerking();

      // Auto-select first enabled option in next step if current step was just selected
      // Disabled states should now be updated after #reapplyPaintFilter()
      this.#autoSelectNextStepIfNeeded(event.target);
      if (!loadsNewProduct) {
        this.#syncCombinedListingOptionSelection();
      }
    });

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || fallbackVariantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct && newUrl) {
      try {
        const connectedUrl = new URL(newUrl, window.location.origin);
        url.pathname = connectedUrl.pathname;
      } catch {
        url.pathname = newUrl.split('?')[0];
      }
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      // Uncheck all other inputs in the same fieldset
      const fieldset = target.closest('fieldset');
      if (fieldset) {
        const allInputs = fieldset.querySelectorAll('input[type="radio"]');

        allInputs.forEach((input) => {
          if (input !== target) {
            input.checked = false;
            input.setAttribute('data-previous-checked', 'false');
            input.setAttribute('data-current-checked', 'false');
          }
        });
      }
      // Set the selected input
      target.checked = true;
      target.setAttribute('data-current-checked', 'true');
      // Update pills visibility
      this.#updatePillsVisibility();
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
        option.setAttribute('data-current-checked', 'false');
      }

      newSelectedOption.setAttribute('selected', 'selected');
      newSelectedOption.setAttribute('data-current-checked', 'true');
    }
  }

  /**
   * Completes an invalid multi-option selection before requesting a new section.
   * Shopify leaves `data-variant-id` empty when the clicked value has no variant with
   * the other currently selected values. In that case, select the first available
   * complete combination containing the clicked value so only one valid request is sent.
   * @param {HTMLElement} selectedOption
   * @returns {string | null}
   * @private
   */
  #selectValidVariantCombination(selectedOption) {
    if (selectedOption.dataset.variantId) return null;

    const optionGroups = Array.from(this.querySelectorAll('[data-option-name]')).filter((group) =>
      group.querySelector('input[type="radio"], select')
    );
    const selectedGroup = selectedOption.closest('[data-option-name]');
    const selectedGroupIndex = optionGroups.indexOf(selectedGroup);
    if (selectedGroupIndex < 0 || optionGroups.length < 2) return null;

    const combinationsScript = this.querySelector('script[data-variant-combinations]');
    if (!(combinationsScript instanceof HTMLScriptElement)) return null;

    try {
      const combinations = JSON.parse(combinationsScript.textContent || '[]');
      const combination = combinations.find(
        (variant) =>
          variant?.available === true &&
          Array.isArray(variant.options) &&
          variant.options[selectedGroupIndex] === selectedOption.value
      );

      if (!combination) return null;

      optionGroups.forEach((group, index) => {
        const optionValue = combination.options[index];
        if (!optionValue || index === selectedGroupIndex) return;

        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
          (input) => input.value === optionValue
        );
        if (radio instanceof HTMLInputElement) {
          this.updateSelectedOption(radio);
          return;
        }

        const select = group.querySelector('select');
        if (select instanceof HTMLSelectElement) {
          const option = Array.from(select.options).find((item) => item.value === optionValue);
          if (option) {
            select.value = optionValue;
            this.updateSelectedOption(select);
          }
        }
      });

      return String(combination.id || '') || null;
    } catch (error) {
      console.error('Unable to resolve a valid variant combination', error);
      return null;
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    // HDS: combined listing naar een ánder product, terwijl die combinatie daar niet bestaat
    // (geen data-variant-id, bv. Smoky vanaf een visgraat-pagina terwijl Smoky alleen stroken
    // heeft). Met de oude keuzes rendert Shopify het nieuwe product zónder variant: «Niet op
    // voorraad» en een knop «Niet beschikbaar». Zonder option_values kiest Shopify zelf de
    // (eerste beschikbare) variant van dat product.
    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const connectedUrl = selectedOption.dataset.connectedProductUrl;
    const toOtherProductWithoutMatch =
      !source && !!connectedUrl && connectedUrl !== currentProductUrl && !selectedOption.dataset.variantId;

    if (toOtherProductWithoutMatch) {
      // Geen option_values: het nieuwe product kiest zijn eigen variant.
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    const requestedOptionValueId = this.selectedOptionId;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          const previousProductId = this.dataset.productId;

          const freshGalleryEl = html.querySelector(PDP_PRIMARY_GALLERY_SELECTOR);
          /**
           * morph() verplaatst DOM-knoppen uit het parse-document naar `document`; daarna is
           * `html.querySelector(media-gallery)` onbruikbaar voor replace.
           */
          const galleryCloneForReplace =
            freshGalleryEl instanceof HTMLElement ? freshGalleryEl.cloneNode(true) : null;

          /**
           * morph() verplaatst nieuw in te voegen knopen úit dit parse-document (geen clone);
           * consumers van event.detail.data.html kregen daardoor een deels leeggegeten document
           * en faalden stil. Clone daarom het hele document vóór de morph — het event krijgt
           * de intacte kopie.
           */
          const htmlForConsumers = /** @type {Document} */ (html.cloneNode(true));

          this.updateMain(html);
          // Clear paint filter when loading new product
          this.#currentPaintFilter = null;

          const variantPayload = JSON.parse(textContent);

          queueMicrotask(() => {
            const picker = this.#getConnectedPicker();

            replaceProductMediaGalleryFromFetchedHtml(html, {
              galleryClone: galleryCloneForReplace,
            });

            picker.#sortVolumeOptions();
            this.#syncCombinedListingOptionSelection();

            /** @type {{ id: string; url: string } | undefined} */
            let newProduct;
            const liveProductId = picker.dataset.productId;
            const liveProductUrl = picker.dataset.productUrl;
            if (
              previousProductId &&
              liveProductId &&
              liveProductUrl &&
              liveProductId !== previousProductId
            ) {
              newProduct = { id: liveProductId, url: liveProductUrl };
            }

            if (requestedOptionValueId) {
              picker.dispatchEvent(
                new VariantUpdateEvent(variantPayload, requestedOptionValueId, {
                  html: htmlForConsumers,
                  productId: picker.dataset.productId ?? '',
                  newProduct,
                })
              );
            }

            // Form-submit opnieuw binden (form-node kan vervangen zijn zonder nieuwe connectedCallback)
            document.querySelectorAll('main product-form-component').forEach((el) => {
              /** @type {any} */ (el).updatedCallback?.();
            });

            picker.#reapplyPaintFilter().then(() => {
              this.#syncCombinedListingOptionSelection();
              picker.#validateAndFixSelection();
            });

            requestAnimationFrame(() => {
              this.#getConnectedPicker().#syncCombinedListingOptionSelection();
            });
          });
        } else {
          const newProduct = this.updateVariantPicker(html);
          this.#syncCombinedListingOptionSelection();

          // We grab the variant object from the response and dispatch an event with it.
          if (requestedOptionValueId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), requestedOptionValueId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
          
          // Reapply paint filter after DOM update
          this.#reapplyPaintFilter().then(() => {
            this.#syncCombinedListingOptionSelection();
            // After paint filter is reapplied, check if the current selection is still valid
            // If not, select the next available option
            this.#validateAndFixSelection();
          });
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.#sortVolumeOptions();
    this.#updateAllFieldsetColumnWidths();

    document.dispatchEvent(
      new CustomEvent('hds:variant-picker-morphed', {
        bubbles: true,
        detail: {
          sectionId: this.dataset.sectionId ?? '',
          productId: this.dataset.productId ?? '',
        },
      })
    );

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
    importMorphedModuleScripts(main);
    if (typeof window.__hdsLoyaltyAfterMainMorph === 'function') {
      window.__hdsLoyaltyAfterMainMorph();
    }
    document.dispatchEvent(
      new CustomEvent('hds:loyalty-dom-reset', { bubbles: true })
    );
    document.dispatchEvent(
      new CustomEvent('hds:main-morphed', { bubbles: true })
    );
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Handle variant change for filtering
   * @param {Event} event
   */
  #handleVariantChangeForFiltering(event) {
    // Only handle variant option changes, not paint filter changes
    if (event.target && /** @type {HTMLElement} */ (event.target).closest('paint-application-filter')) {
      return;
    }

    // Update pills visibility based on checked state
    this.#updatePillsVisibility();

    // Update step 3 based on step 2 selection (progressive filtering)
    this.#updateStep3BasedOnStep2();
  }

  /**
   * Update pills visibility based on checked state and data attributes
   * @private
   */
  #updatePillsVisibility() {
    const variantSteps = this.querySelectorAll('fieldset.variant-option');
    
    variantSteps.forEach((stepFieldset) => {
      const pills = stepFieldset.querySelectorAll('.variant-option__button-label__pill');
      
      pills.forEach((pill) => {
        const label = pill.closest('.variant-option__button-label');
        const input = label?.querySelector('input[type="radio"], option');
        
        if (input) {
          const isChecked = input.checked;
          const prevChecked = input.getAttribute('data-previous-checked');
          const currentChecked = input.getAttribute('data-current-checked');
          const shouldShowPill = isChecked || prevChecked === 'true' || currentChecked === 'true';
          if (!shouldShowPill) {
            // Hide pill by moving it out of view
            label.style.setProperty('--pill-offset', 'calc(-100% - 1px)');
          } else {
            // Show pill by resetting offset (let CSS handle the positioning)
            label.style.removeProperty('--pill-offset');
          }
        }
      });
    });
  }

  /**
   * Handle paint filter changed event
   * @param {PaintFilterChangedEvent} event
   */
  #handlePaintFilterChanged(event) {
    
    const { application } = event.detail;
    
    if (!application) {
      return;
    }
    
    this.#currentPaintFilter = application;
    // When paint filter changes, we should clear selections
    this.#filterVariantsByApplication(application, true);
  }

  /**
   * Handle paint filter cleared event
   */
  #handlePaintFilterCleared() {
    // Clear the current filter state
    this.#currentPaintFilter = null;
    this.#showAllVariants();
  }

  /**
   * Filter all steps by application only (progressive filtering)
   * When step 1 (paint application) is selected:
   * - Step 1 (variant step 1, index 0 = "Afwerking") should be filtered by application
   *   - At initial load: show all, disable incompatible
   *   - After user interaction: hide incompatible
   * - Step 2 (variant step 2, index 1 = "Inhoud") should be filtered by application
   *   - At initial load: show all, disable incompatible
   *   - After user interaction: hide incompatible
   * - Step 3 and beyond should show all options (not filtered yet)
   * Only when step 2 is selected should step 3 be filtered
   * @param {string} application - The paint application to filter by
   */
  #filterStepByApplicationOnly(application, shouldClearSelections = false) {
    
    // Get all variant option fieldsets
    const variantSteps = this.querySelectorAll('fieldset.variant-option');
    
    // Filter Step 1 (index 0 = "Afwerking") by application
    if (variantSteps.length > 0) {
      const step1Fieldset = variantSteps[0];
      
      // At initial load: disable incompatible options (show all, disable incompatible)
      // After user interaction: hide incompatible options
      if (this.#allowAutoSelection) {
        this.#filterStepByApplicationWithDisabled(/** @type {HTMLElement} */ (step1Fieldset), application, 'Step 1 (Afwerking)');
      } else {
        this.#filterStepByApplicationWithoutAutoSelect(/** @type {HTMLElement} */ (step1Fieldset), application, 'Step 1 (Afwerking)');
      }
    }
    
    // Filter Step 2 (index 1 = "Inhoud") by application
    if (variantSteps.length > 1) {
      const step2Fieldset = variantSteps[1];
      
      // At initial load: disable incompatible options (show all, disable incompatible)
      // After user interaction: hide incompatible options
      if (this.#allowAutoSelection) {
        this.#filterStepByApplicationWithDisabled(/** @type {HTMLElement} */ (step2Fieldset), application, 'Step 2 (Inhoud)');
      } else {
        this.#filterStepByApplicationWithoutAutoSelect(/** @type {HTMLElement} */ (step2Fieldset), application, 'Step 2 (Inhoud)');
      }
    }
    
    // Handle step 3 and beyond with progressive filtering
    // Step 3 should only be enabled if step 2 (index 1) has a selection
    if (variantSteps.length > 2) {
      // Check if step 2 has a selected option
      const step2Fieldset = variantSteps[1];
      const step2Selected = step2Fieldset.querySelector('input:checked, option:checked');
      const step2HasSelection = !!step2Selected;
      
      
      // Step 3 and beyond
      for (let i = 2; i < variantSteps.length; i++) {
        const stepFieldset = variantSteps[i];
        const stepOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
        
        stepOptions.forEach((option) => {
          option.classList.remove('variant-option--filtered-out');
          option.classList.remove('variant-option--hidden-by-liquid');
          const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
          
          if (input) {
            // Store original availability if not already stored
            if (!option.dataset.originalAvailable) {
              const dataAvailable = input.getAttribute('data-option-available');
              option.dataset.originalAvailable = dataAvailable || 'true';
            }
            
            // Disable all options in step 3 if step 2 is not selected
            if (!step2HasSelection) {
              input.disabled = true;
              input.setAttribute('aria-disabled', 'true');
              input.setAttribute('data-option-available', 'false');
              
              // Add tooltip explaining why this option is disabled (progressive filtering)
              const variantName = input.value || option.textContent?.trim() || 'deze optie';
              const step2Legend = step2Fieldset.querySelector('legend');
              const step2Name = step2Legend?.textContent?.trim() || 'de vorige stap';
              const tooltipText = `${variantName} is niet beschikbaar omdat er eerst een keuze gemaakt moet worden in ${step2Name.toLowerCase()}.`;
              
              // Remove title attribute (we'll use custom tooltip)
              option.removeAttribute('title');
              input.removeAttribute('title');
              
              // Create or update tooltip element
              // Place tooltip in document.body to avoid overflow clipping from parent
              // Ensure option has an ID for tooltip reference
              if (!option.id) {
                option.id = `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              }
              
              // Find existing tooltip in document.body
              let tooltip = document.querySelector(`.variant-option__tooltip[data-option-ref="${option.id}"]`);
              if (!tooltip) {
                tooltip = document.createElement('div');
                tooltip.className = 'variant-option__tooltip';
                tooltip.setAttribute('role', 'tooltip');
                // Store reference to option on tooltip for positioning
                tooltip.dataset.optionRef = option.id;
                document.body.appendChild(tooltip);
              }
              tooltip.textContent = tooltipText;
              
              // Ensure tooltip is hidden initially
              tooltip.classList.remove('variant-option__tooltip--visible');
              
              // Add event listeners for showing/hiding tooltip
              this.#setupTooltipEvents(/** @type {HTMLElement} */ (option), tooltip);
              
              // Add strikethrough if not already present
              if (!option.querySelector('.variant-option__strikethrough')) {
                const strikethrough = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                strikethrough.setAttribute('viewBox', '0 0 100 46');
                strikethrough.setAttribute('preserveAspectRatio', 'xMidYMid slice');
                strikethrough.classList.add('variant-option__strikethrough');
                
                const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                line1.setAttribute('x1', '100');
                line1.setAttribute('y1', '0');
                line1.setAttribute('x2', '0');
                line1.setAttribute('y2', '46');
                line1.setAttribute('vector-effect', 'non-scaling-stroke');
                
                const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                line2.setAttribute('x1', '100');
                line2.setAttribute('y1', '0');
                line2.setAttribute('x2', '0');
                line2.setAttribute('y2', '46');
                line2.setAttribute('vector-effect', 'non-scaling-stroke');
                
                strikethrough.appendChild(line1);
                strikethrough.appendChild(line2);
                option.appendChild(strikethrough);
              }
            } else {
            // Remove tooltip if option is enabled
            // Tooltip might be in option or in fieldset
            const fieldset = option.closest('fieldset.variant-option');
            const optionId = option.id;
            let tooltip = option.querySelector('.variant-option__tooltip');
            if (!tooltip && fieldset && optionId) {
              tooltip = fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${optionId}"]`);
            }
            if (tooltip) {
              tooltip.remove();
            }
              // Remove event listeners
              if (option._tooltipShowHandler) {
                option.removeEventListener('mouseenter', option._tooltipShowHandler);
                option.removeEventListener('mouseleave', option._tooltipHideHandler);
                delete option._tooltipShowHandler;
                delete option._tooltipHideHandler;
              }
              // Step 2 is selected, enable compatible options
              const originalAvailable = option.dataset.originalAvailable || 'true';
              input.setAttribute('data-option-available', originalAvailable);
              
              if (originalAvailable === 'true') {
                input.disabled = false;
                input.removeAttribute('aria-disabled');
                // Remove strikethrough if it was added by us
                const strikethrough = option.querySelector('.variant-option__strikethrough');
                if (strikethrough) {
                  strikethrough.remove();
                }
              } else {
                // Keep original unavailable state
                input.disabled = true;
                input.setAttribute('aria-disabled', 'true');
              }
            }
          }
        });
        
      }
    }
    
    // Only auto-select if allowed (i.e., during initial load, not after user interaction)
    if (this.#allowAutoSelection) {
      // Use requestAnimationFrame to ensure DOM updates are complete before auto-selection
      requestAnimationFrame(() => {
        this.#autoSelectStepsSequentially(variantSteps, application);
      });
    } else {
      // Only re-select when the paint filter changes, not when reapplying after a variant click
      if (shouldClearSelections) {
        // SHO-8475: When the paint application (Geschikte ruimte) changes after the user has
        // already interacted, don't clear every selection. Clearing left the variant incomplete,
        // so the (native Horizon) add-to-cart button became disabled with no way forward.
        // Instead, re-run the same auto-selection used on initial load: it keeps the current
        // option when it's still valid for the new application, otherwise it selects the first
        // available option per step. This keeps the variant complete and the button enabled.
        requestAnimationFrame(() => {
          const previousAllowAutoSelection = this.#allowAutoSelection;
          this.#allowAutoSelection = true;
          this.#autoSelectStepsSequentially(variantSteps, application);
          this.#allowAutoSelection = previousAllowAutoSelection;
        });
      } else {
        // Update pills visibility to show current selections
        this.#updatePillsVisibility();
      }
      
      // Update visual state for steps after afwerking
      this.#updateStepsAfterAfwerking();
    }

    this.#updateAllFieldsetColumnWidths();
  }

  /**
   * Auto-select first enabled option in next step when a variant option is selected
   * This ensures that when an afwerking is selected, the first enabled inhoud option is automatically selected
   * @param {HTMLElement} selectedInput - The input that was just selected
   * @private
   */
  #autoSelectNextStepIfNeeded(selectedInput) {
    // Don't auto-select if auto-selection is disabled
    if (!this.#allowAutoSelection) {
      return;
    }
    
    if (!selectedInput) {
      return;
    }
    
    
    // Find which step this input belongs to
    const fieldset = selectedInput.closest('fieldset.variant-option');
    if (!fieldset) {
      return;
    }
    
    const variantSteps = this.querySelectorAll('fieldset.variant-option');
    const currentStepIndex = Array.from(variantSteps).indexOf(fieldset);
    
    
    // Only auto-select next step if we're selecting step 1 (afwerking, index 0)
    // and step 2 (inhoud, index 1) exists and has no selection
    if (currentStepIndex === 0 && variantSteps.length > 1) {
      const nextStepFieldset = variantSteps[1];
      const nextStepHasSelection = !!nextStepFieldset.querySelector('input:checked, option:checked');
      
      
      // Only auto-select if next step has no selection
      if (!nextStepHasSelection) {
        
        // Get the selected afwerking value to check compatibility
        const selectedAfwerkingValue = selectedInput.value;
        
        // Find first visible AND enabled option in next step
        const allOptions = nextStepFieldset.querySelectorAll('.variant-option__button-label');
        
        let firstVisibleEnabledInDOM = null;
        
        for (let i = 0; i < allOptions.length; i++) {
          const option = allOptions[i];
          const isHidden = option.classList.contains('variant-option--hidden-by-liquid') || 
                          option.classList.contains('variant-option--filtered-out') ||
                          option.classList.contains('variant-option--waiting-for-previous-step');
          const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
          const isDisabled = input?.disabled === true;
          const ariaDisabled = input?.getAttribute('aria-disabled') === 'true';
          const dataAvailable = input?.getAttribute('data-option-available');
          const isAvailable = dataAvailable !== 'false';
          
          // Also check original availability from dataset (stored during filtering)
          // If originalAvailable is undefined, consider it as available (not yet set)
          // If originalAvailable is 'false', consider it as unavailable
          const originalAvailable = option.dataset.originalAvailable;
          const isOriginallyAvailable = originalAvailable === undefined || originalAvailable !== 'false';
          
          
          // Only consider options that are visible AND enabled (not disabled)
          // Check disabled attribute, aria-disabled attribute, data-option-available, and originalAvailable
          // CRITICAL: originalAvailable must be 'true' or undefined (not 'false')
          // NOTE: We don't check compatibility here - the variant-picker will handle that after selection
          // If the combination is invalid, the variant-picker will handle it appropriately
          const isSelectable = !isHidden && !isDisabled && !ariaDisabled && isAvailable && isOriginallyAvailable;
          
          if (isSelectable) {
            firstVisibleEnabledInDOM = option;
            break;
          }
        }
        
        if (firstVisibleEnabledInDOM) {
          const input = firstVisibleEnabledInDOM.querySelector('input[type="radio"]') || firstVisibleEnabledInDOM.querySelector('option');
          
          if (input) {
            input.checked = true;
            
            // Trigger change event to update the variant
            const changeEvent = new Event('change', { bubbles: true });
            input.dispatchEvent(changeEvent);
          }
        }
      }
    }
  }

  /**
   * Na morph(main) kan dit element vervangen zijn; gebruik de live picker in de DOM.
   * @returns {VariantPicker}
   * @private
   */
  #getConnectedPicker() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return this;

    const livePicker = document.querySelector(
      `variant-picker[data-section-id="${sectionId}"][data-template-product-match="true"]`
    );

    return livePicker instanceof VariantPicker ? livePicker : this;
  }

  /**
   * @param {HTMLFieldSetElement} fieldset
   * @returns {boolean}
   * @private
   */
  #isCombinedListingFieldset(fieldset) {
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]')).filter(
      (input) => input instanceof HTMLInputElement
    );

    if (radios.length < 2) return false;

    const linkedRadios = radios.filter((input) => input.dataset.connectedProductUrl);
    return linkedRadios.length >= 1;
  }

  /**
   * @param {HTMLFieldSetElement} fieldset
   * @param {string} currentPath
   * @param {string} currentHandle
   * @returns {HTMLInputElement | undefined}
   * @private
   */
  #findCombinedListingInputForCurrentProduct(fieldset, currentPath, currentHandle) {
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]')).filter(
      (input) => input instanceof HTMLInputElement
    );

    const pathMatch = radios.find((input) => {
      const connectedUrl = input.dataset.connectedProductUrl;
      return connectedUrl && normalizeProductPath(connectedUrl) === currentPath;
    });
    if (pathMatch instanceof HTMLInputElement) return pathMatch;

    if (currentHandle) {
      const handleMatch = radios.find((input) => {
        const connectedUrl = input.dataset.connectedProductUrl;
        return connectedUrl && extractProductHandle(connectedUrl) === currentHandle;
      });
      if (handleMatch instanceof HTMLInputElement) return handleMatch;
    }

    const unlinkedRadios = radios.filter((input) => !input.dataset.connectedProductUrl);
    const linkedRadios = radios.filter((input) => input.dataset.connectedProductUrl);
    if (unlinkedRadios.length === 1 && linkedRadios.length >= 1) {
      return unlinkedRadios[0];
    }

    if (this.#pendingCombinedListingOptionValue) {
      const pendingValue = this.#pendingCombinedListingOptionValue;
      const valueMatch = radios.find((input) => input.value === pendingValue);
      if (valueMatch instanceof HTMLInputElement) return valueMatch;
    }

    return undefined;
  }

  /**
   * Combined listings: zorg dat de optie die naar het huidige child-product wijst visueel geselecteerd is.
   * @private
   */
  #syncCombinedListingOptionSelection() {
    const picker = this.#getConnectedPicker();
    const productUrl = picker.dataset.productUrl || window.location.pathname;
    const currentPath = normalizeProductPath(productUrl);
    const currentHandle = extractProductHandle(productUrl) || extractProductHandle(window.location.pathname);

    if (!currentPath && !currentHandle) return;

    const fieldsets = picker.querySelectorAll('fieldset.variant-option');

    for (const fieldset of fieldsets) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;
      if (!this.#isCombinedListingFieldset(fieldset)) continue;

      const matchingInput = this.#findCombinedListingInputForCurrentProduct(
        fieldset,
        currentPath,
        currentHandle
      );

      if (!(matchingInput instanceof HTMLInputElement)) continue;

      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedInput === matchingInput) continue;

      picker.updateSelectedOption(matchingInput);

      if (fieldset.classList.contains('variant-option--swatches')) {
        picker.#updateSwatchValueDisplay(fieldset, matchingInput);
      }
    }

    picker.#updatePillsVisibility();
    this.#pendingCombinedListingOptionValue = null;
  }

  /**
   * Validate the current selection and fix it if necessary
   * After DOM updates, check if the selected options are still valid
   * If a selected option is no longer available, select the next available option
   * @private
   */
  #validateAndFixSelection() {
    const variantSteps = this.querySelectorAll('fieldset.variant-option');
    if (variantSteps.length < 2) return;
    
    // Check step 2 (inhoud) - the last step
    const step2Fieldset = variantSteps[variantSteps.length - 1];
    const selectedInput = step2Fieldset.querySelector('input:checked');
    
    if (!selectedInput) return;
    
    const option = selectedInput.closest('.variant-option__button-label');
    if (!option) return;
    
    // Check if the selected option is still available
    const isHidden = option.classList.contains('variant-option--hidden-by-liquid') || 
                    option.classList.contains('variant-option--filtered-out') ||
                    option.classList.contains('variant-option--waiting-for-previous-step');
    const isDisabled = selectedInput.disabled === true;
    const ariaDisabled = selectedInput.getAttribute('aria-disabled') === 'true';
    const dataAvailable = selectedInput.getAttribute('data-option-available');
    const isAvailable = dataAvailable !== 'false';
    const originalAvailable = option.dataset.originalAvailable;
    const isOriginallyAvailable = originalAvailable === undefined || originalAvailable !== 'false';
    
    const isStillValid = !isHidden && !isDisabled && !ariaDisabled && isAvailable && isOriginallyAvailable;
    
    
    if (!isStillValid) {
      
      // Find the first available option in step 2
      const allOptions = step2Fieldset.querySelectorAll('.variant-option__button-label');
      let nextAvailableOption = null;
      
      for (let i = 0; i < allOptions.length; i++) {
        const opt = allOptions[i];
        const optInput = opt.querySelector('input[type="radio"]') || opt.querySelector('option');
        if (!optInput) continue;
        
        const optIsHidden = opt.classList.contains('variant-option--hidden-by-liquid') || 
                           opt.classList.contains('variant-option--filtered-out') ||
                           opt.classList.contains('variant-option--waiting-for-previous-step');
        const optIsDisabled = optInput.disabled === true;
        const optAriaDisabled = optInput.getAttribute('aria-disabled') === 'true';
        const optDataAvailable = optInput.getAttribute('data-option-available');
        const optIsAvailable = optDataAvailable !== 'false';
        const optOriginalAvailable = opt.dataset.originalAvailable;
        const optIsOriginallyAvailable = optOriginalAvailable === undefined || optOriginalAvailable !== 'false';
        
        const optIsSelectable = !optIsHidden && !optIsDisabled && !optAriaDisabled && optIsAvailable && optIsOriginallyAvailable;
        
        if (optIsSelectable) {
          nextAvailableOption = optInput;
          break;
        }
      }
      
      if (nextAvailableOption) {
        // Always select the next available option when current selection is invalid
        // This is not "auto-selection" but a correction of an invalid state
        nextAvailableOption.checked = true;
        
        // Trigger change event to update the variant
        const changeEvent = new Event('change', { bubbles: true });
        nextAvailableOption.dispatchEvent(changeEvent);
      }
    }
  }

  /**
   * Check if an option (inhoud) is compatible with the selected afwerking
   * This checks if there's a variant that has both the selected afwerking AND the inhoud option
   * @param {string|undefined} optionVariantId - The variant ID from the option (if available)
   * @param {string} selectedAfwerkingValue - The selected afwerking value
   * @param {string} inhoudValue - The inhoud option value to check
   * @returns {boolean} - Whether the option is compatible with the selected afwerking
   * @private
   */
  #isOptionCompatibleWithAfwerking(optionVariantId, selectedAfwerkingValue, inhoudValue) {
    
    const variantSteps = this.querySelectorAll('fieldset.variant-option');
    if (variantSteps.length < 2) {
      return false;
    }
    
    const step1Fieldset = variantSteps[0];
    const step2Fieldset = variantSteps[1];
    
    // Strategy 1: If we have a variant ID, check if that variant has both values
    if (optionVariantId && optionVariantId !== '(no variant ID)') {
      // Find all inputs with this variant ID in step 1
      const step1Inputs = step1Fieldset.querySelectorAll(`input[data-variant-id="${optionVariantId}"]`);
      let hasAfwerking = false;
      
      for (const step1Input of step1Inputs) {
        if (step1Input.value === selectedAfwerkingValue) {
          hasAfwerking = true;
          break;
        }
      }
      
      if (hasAfwerking) {
        // Check if this variant also has the inhoud value in step 2
        const step2Inputs = step2Fieldset.querySelectorAll(`input[data-variant-id="${optionVariantId}"]`);
        for (const step2Input of step2Inputs) {
          if (step2Input.value === inhoudValue) {
            return true;
          }
        }
      }
    }
    
    // Strategy 2: Find all inputs in step 1 with the selected afwerking value
    // Then check if any of those variants also have the inhoud value in step 2
    const step1Inputs = step1Fieldset.querySelectorAll(`input[value="${selectedAfwerkingValue}"]`);
    
    for (const step1Input of step1Inputs) {
      const step1VariantId = step1Input.dataset.variantId;
      
      if (step1VariantId && step1VariantId !== '(no variant ID)') {
        // Check if there's an input in step 2 with the same variant ID and the inhoud value
        const step2Inputs = step2Fieldset.querySelectorAll(`input[data-variant-id="${step1VariantId}"][value="${inhoudValue}"]`);
        if (step2Inputs.length > 0) {
          return true;
        }
      } else {
        // No variant ID - check if the option value itself matches
        // This handles cases where options don't have variant IDs
        // We need to check if there's a way to determine compatibility
        // For now, if there's no variant ID, we'll assume it's compatible if it's visible and enabled
      }
    }
    
    // Strategy 3: If no variant IDs are available, check if the option is actually available
    // by checking if it's disabled or has data-option-available='false'
    // This is a fallback for when variant IDs are not available
    const step2Inputs = step2Fieldset.querySelectorAll(`input[value="${inhoudValue}"]`);
    for (const step2Input of step2Inputs) {
      const isDisabled = step2Input.disabled === true;
      const ariaDisabled = step2Input.getAttribute('aria-disabled') === 'true';
      const dataAvailable = step2Input.getAttribute('data-option-available');
      const isAvailable = dataAvailable !== 'false';
      
      // If the option is not disabled and is available, and we can't find a variant ID match,
      // we'll assume it might be compatible (but this is less reliable)
      if (!isDisabled && !ariaDisabled && isAvailable) {
        // Don't return true here - we need to be more careful
        // Instead, we'll return false and let the disabled state check handle it
      }
    }
    
    return false;
  }

  /**
   * Update all steps after afwerking (step 1) based on whether afwerking is selected
   * Steps after afwerking should have a special visual state (opacity + tooltip) if afwerking is not selected
   * @private
   */
  #updateStepsAfterAfwerking() {
    const variantSteps = this.querySelectorAll('fieldset.variant-option');
    
    // Only proceed if we have at least 2 steps (step 1 = afwerking, step 2+ = stappen na afwerking)
    if (variantSteps.length < 2) {
      return;
    }
    
    // Check if step 1 (afwerking, index 0) has a selected option
    const step1Fieldset = variantSteps[0];
    const step1Selected = step1Fieldset.querySelector('input:checked, option:checked');
    const step1HasSelection = !!step1Selected;
    
    
    // Update all steps after afwerking (index >= 1)
    for (let i = 1; i < variantSteps.length; i++) {
      const stepFieldset = variantSteps[i];
      const stepOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
      const stepLegend = stepFieldset.querySelector('legend');
      const stepName = stepLegend?.textContent?.trim() || `stap ${i + 1}`;
      
      stepOptions.forEach((option) => {
        const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
        
        if (input) {
          if (!step1HasSelection) {
            // Step 1 (afwerking) is not selected - add special visual state
            option.classList.add('variant-option--waiting-for-previous-step');
            
            // Don't disable the input, but prevent interaction via CSS (pointer-events: none)
            // The input should remain enabled so it can be selected programmatically if needed
            
            // Add tooltip explaining why this option is not selectable
            const variantName = input.value || option.textContent?.trim() || 'deze optie';
            const tooltipText = `Selecteer eerst een afwerking`;
            
            // Remove title attribute (we'll use custom tooltip)
            option.removeAttribute('title');
            input.removeAttribute('title');
            
            // Create or update tooltip element
            const fieldset = option.closest('fieldset.variant-option');
            if (!option.id) {
              option.id = `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            }
            let tooltip = option.querySelector('.variant-option__tooltip') || (fieldset ? fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${option.id}"]`) : null);
            if (!tooltip) {
              tooltip = document.createElement('div');
              tooltip.className = 'variant-option__tooltip';
              tooltip.setAttribute('role', 'tooltip');
              tooltip.dataset.optionRef = option.id;
              if (fieldset) {
                fieldset.appendChild(tooltip);
              } else {
                document.body.appendChild(tooltip);
              }
            }
            tooltip.textContent = tooltipText;
            
            // Ensure tooltip is hidden initially
            tooltip.classList.remove('variant-option__tooltip--visible');
            
            // Add event listeners for showing/hiding tooltip
            this.#setupTooltipEvents(/** @type {HTMLElement} */ (option), tooltip);
            
          } else {
            // Step 1 (afwerking) is selected - remove special visual state
            option.classList.remove('variant-option--waiting-for-previous-step');
            
            // Remove tooltip if it exists
            const fieldset = option.closest('fieldset.variant-option');
            const optionId = option.id;
            let tooltip = option.querySelector('.variant-option__tooltip');
            if (!tooltip && fieldset && optionId) {
              tooltip = fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${optionId}"]`);
            }
            if (tooltip) {
              // Only remove tooltip if it's the "selecteer eerst afwerking" tooltip
              if (tooltip.textContent?.includes('Selecteer eerst een afwerking')) {
                tooltip.remove();
                // Remove event listeners
                if (option._tooltipShowHandler) {
                  option.removeEventListener('mouseenter', option._tooltipShowHandler);
                  option.removeEventListener('mouseleave', option._tooltipHideHandler);
                  delete option._tooltipShowHandler;
                  delete option._tooltipHideHandler;
                }
              }
            }
          }
        }
      });
    }
  }

  /**
   * Update step 3 based on step 2 selection (progressive filtering)
   * Step 3 should be disabled if step 2 is not selected
   * Also check paint filter compatibility and set appropriate tooltips
   * @private
   */
  #updateStep3BasedOnStep2() {
    const variantSteps = this.querySelectorAll('fieldset.variant-option');
    
    // Only proceed if we have at least 3 steps (step 1, step 2, step 3)
    if (variantSteps.length < 3) {
      return;
    }
    
    // Get current paint filter application
    const paintFilter = document.querySelector('paint-application-filter');
    const checkedPaintFilterInput = paintFilter?.querySelector('input:checked');
    const currentApplication = checkedPaintFilterInput ? (checkedPaintFilterInput.value || checkedPaintFilterInput.getAttribute('data-application')) : null;
    
    // Check if step 2 (index 1) has a selected option
    const step2Fieldset = variantSteps[1];
    const step2Selected = step2Fieldset.querySelector('input:checked, option:checked');
    const step2HasSelection = !!step2Selected;
    
    
    // Step 3 (index 2)
    const step3Fieldset = variantSteps[2];
    const step3Options = step3Fieldset.querySelectorAll('.variant-option__button-label');
    
    step3Options.forEach((option) => {
      const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
      
      if (input) {
        // Store original availability if not already stored
        if (!option.dataset.originalAvailable) {
          const dataAvailable = input.getAttribute('data-option-available');
          option.dataset.originalAvailable = dataAvailable || 'true';
        }
        
        // Check paint filter compatibility
        const optionApplication = /** @type {HTMLElement} */ (option).dataset.application;
        let applications = [];
        if (optionApplication && currentApplication) {
          try {
            const parsed = JSON.parse(optionApplication);
            if (Array.isArray(parsed)) {
              applications = parsed;
            } else {
              applications = [parsed];
            }
          } catch (e) {
            applications = optionApplication.split(',').map(app => app.trim()).filter(app => app !== '');
          }
        }
        const matchesPaintFilter = !currentApplication || applications.includes(currentApplication);
        
        // Disable all options in step 3 if step 2 is not selected
        if (!step2HasSelection) {
          input.disabled = true;
          input.setAttribute('aria-disabled', 'true');
          input.setAttribute('data-option-available', 'false');
          
          // Add tooltip explaining why this option is disabled (progressive filtering)
          const variantName = input.value || option.textContent?.trim() || 'deze optie';
          const step2Legend = step2Fieldset.querySelector('legend');
          const step2Name = step2Legend?.textContent?.trim() || 'de vorige stap';
          const tooltipText = `${variantName} is niet beschikbaar omdat er eerst een keuze gemaakt moet worden in ${step2Name.toLowerCase()}.`;
          
          // Remove title attribute (we'll use custom tooltip)
          option.removeAttribute('title');
          input.removeAttribute('title');
          
          // Create or update tooltip element
          // Place tooltip in fieldset (parent) to avoid overflow clipping from label
          const fieldset = option.closest('fieldset.variant-option');
          if (!option.id) {
            option.id = `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          }
          let tooltip = option.querySelector('.variant-option__tooltip') || (fieldset ? fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${option.id}"]`) : null);
          if (!tooltip) {
            tooltip = document.createElement('div');
            tooltip.className = 'variant-option__tooltip';
            tooltip.setAttribute('role', 'tooltip');
            // Store reference to option for positioning
            tooltip.dataset.optionRef = option.id;
            // Place in fieldset if available, otherwise in document.body
            if (fieldset) {
              fieldset.appendChild(tooltip);
            } else {
              document.body.appendChild(tooltip);
            }
          }
          tooltip.textContent = tooltipText;
          
          // Ensure tooltip is hidden initially
          tooltip.classList.remove('variant-option__tooltip--visible');
          
          // Add event listeners for showing/hiding tooltip
          this.#setupTooltipEvents(/** @type {HTMLElement} */ (option), tooltip);
          
          
          // Add strikethrough if not already present
          if (!option.querySelector('.variant-option__strikethrough')) {
            const strikethrough = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            strikethrough.setAttribute('viewBox', '0 0 100 46');
            strikethrough.setAttribute('preserveAspectRatio', 'xMidYMid slice');
            strikethrough.classList.add('variant-option__strikethrough');
            
            const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line1.setAttribute('x1', '100');
            line1.setAttribute('y1', '0');
            line1.setAttribute('x2', '0');
            line1.setAttribute('y2', '46');
            line1.setAttribute('vector-effect', 'non-scaling-stroke');
            
            const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line2.setAttribute('x1', '100');
            line2.setAttribute('y1', '0');
            line2.setAttribute('x2', '0');
            line2.setAttribute('y2', '46');
            line2.setAttribute('vector-effect', 'non-scaling-stroke');
            
            strikethrough.appendChild(line1);
            strikethrough.appendChild(line2);
            option.appendChild(strikethrough);
          }
        } else {
          // Step 2 is selected, check paint filter compatibility and original availability
          const originalAvailable = option.dataset.originalAvailable || 'true';
          
          // Check if option should be disabled due to paint filter incompatibility
          if (!matchesPaintFilter && currentApplication) {
            // Disable because not compatible with paint filter
            input.disabled = true;
            input.setAttribute('aria-disabled', 'true');
            input.setAttribute('data-option-available', 'false');
            
            // Add tooltip explaining why this option is disabled (paint filter)
            const variantName = input.value || option.textContent?.trim() || 'deze optie';
            const paintFilterTitle = this.#getPaintFilterTitle().toLowerCase();
            const tooltipText = `${variantName} is niet beschikbaar door de huidige selectie van ${paintFilterTitle}.`;
            
            // Remove title attribute (we'll use custom tooltip)
            option.removeAttribute('title');
            input.removeAttribute('title');
            
            // Create or update tooltip element
            // Place tooltip in fieldset (parent) to avoid overflow clipping from label
            const fieldset = option.closest('fieldset.variant-option');
            if (!option.id) {
              option.id = `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            }
            let tooltip = option.querySelector('.variant-option__tooltip') || (fieldset ? fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${option.id}"]`) : null);
            if (!tooltip) {
              tooltip = document.createElement('div');
              tooltip.className = 'variant-option__tooltip';
              tooltip.setAttribute('role', 'tooltip');
              // Store reference to option for positioning
              tooltip.dataset.optionRef = option.id;
              // Place in fieldset if available, otherwise in document.body
              if (fieldset) {
                fieldset.appendChild(tooltip);
              } else {
                document.body.appendChild(tooltip);
              }
            }
            tooltip.textContent = tooltipText;
            
            // Ensure tooltip is hidden initially
            tooltip.classList.remove('variant-option__tooltip--visible');
            
            // Add event listeners for showing/hiding tooltip
            this.#setupTooltipEvents(/** @type {HTMLElement} */ (option), tooltip);
            
            // Add strikethrough if not already present
            if (!option.querySelector('.variant-option__strikethrough')) {
              const strikethrough = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              strikethrough.setAttribute('viewBox', '0 0 100 46');
              strikethrough.setAttribute('preserveAspectRatio', 'xMidYMid slice');
              strikethrough.classList.add('variant-option__strikethrough');
              
              const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
              line1.setAttribute('x1', '100');
              line1.setAttribute('y1', '0');
              line1.setAttribute('x2', '0');
              line1.setAttribute('y2', '46');
              line1.setAttribute('vector-effect', 'non-scaling-stroke');
              
              const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
              line2.setAttribute('x1', '100');
              line2.setAttribute('y1', '0');
              line2.setAttribute('x2', '0');
              line2.setAttribute('y2', '46');
              line2.setAttribute('vector-effect', 'non-scaling-stroke');
              
              strikethrough.appendChild(line1);
              strikethrough.appendChild(line2);
              option.appendChild(strikethrough);
            }
          } else if (originalAvailable === 'true') {
            // Enable compatible options
            input.disabled = false;
            input.removeAttribute('aria-disabled');
            input.setAttribute('data-option-available', 'true');
            
            // Remove tooltip if it exists
            // Tooltip might be in option or in fieldset
            const fieldset = option.closest('fieldset.variant-option');
            const optionId = option.id;
            let tooltip = option.querySelector('.variant-option__tooltip');
            if (!tooltip && fieldset && optionId) {
              tooltip = fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${optionId}"]`);
            }
            if (tooltip) {
              tooltip.remove();
            }
            // Remove event listeners
            if (option._tooltipShowHandler) {
              option.removeEventListener('mouseenter', option._tooltipShowHandler);
              option.removeEventListener('mouseleave', option._tooltipHideHandler);
              delete option._tooltipShowHandler;
              delete option._tooltipHideHandler;
            }
            // Remove strikethrough if it was added by us
            const strikethrough = option.querySelector('.variant-option__strikethrough');
            if (strikethrough) {
              strikethrough.remove();
            }
          } else {
            // Keep original unavailable state (disabled from Liquid)
            input.disabled = true;
            input.setAttribute('aria-disabled', 'true');
            input.setAttribute('data-option-available', 'false');
            // Don't add tooltip for originally unavailable options
          }
        }
      }
    });
    
    
    // Also update visual state for steps after afwerking
    this.#updateStepsAfterAfwerking();
  }

  /**
   * Clear selections in steps after the first one (for progressive filtering)
   * When paint filter changes, clear all variant step selections so user must choose again
   * @param {NodeList} variantSteps - All variant step fieldsets
   * @private
   */
  #clearSelectionsInSubsequentSteps(variantSteps) {
    // Clear selections in ALL variant steps (step 1, step 2, step 3, etc.)
    // User must manually select each step after paint filter changes
    for (let i = 0; i < variantSteps.length; i++) {
      const stepFieldset = variantSteps[i];
      const checkedInputs = stepFieldset.querySelectorAll('input:checked, option:checked');
      
      checkedInputs.forEach((input) => {
        input.checked = false;
        // Also reset the data attributes that control pill visibility
        input.removeAttribute('data-previous-checked');
        input.removeAttribute('data-current-checked');
        input.setAttribute('data-previous-checked', 'false');
        input.setAttribute('data-current-checked', 'false');
        
      });
      
      // Also clear any unchecked inputs that might have these attributes set
      const allInputs = stepFieldset.querySelectorAll('input[type="radio"], option');
      
      allInputs.forEach((input) => {
        const prevChecked = input.getAttribute('data-previous-checked');
        const currentChecked = input.getAttribute('data-current-checked');
        
        if (prevChecked === 'true' || currentChecked === 'true') {
          input.removeAttribute('data-previous-checked');
          input.removeAttribute('data-current-checked');
          input.setAttribute('data-previous-checked', 'false');
          input.setAttribute('data-current-checked', 'false');
        }
      });
      
      
      // Hide pills for inputs that are not checked and don't have data attributes set to 'true'
      // Use CSS variable --pill-offset to hide pills (move them out of view)
      const pills = stepFieldset.querySelectorAll('.variant-option__button-label__pill');
      
      pills.forEach((pill) => {
        const label = pill.closest('.variant-option__button-label');
        const input = label?.querySelector('input[type="radio"], option');
        
        if (input) {
          const isChecked = input.checked;
          const prevChecked = input.getAttribute('data-previous-checked');
          const currentChecked = input.getAttribute('data-current-checked');
          const shouldShowPill = isChecked || prevChecked === 'true' || currentChecked === 'true';
          
          
          if (!shouldShowPill) {
            // Hide pill by moving it out of view using CSS variable
            label.style.setProperty('--pill-offset', 'calc(-100% - 1px)');
          } else {
            // Reset pill offset to let CSS handle it (pill will be visible)
            label.style.removeProperty('--pill-offset');
          }
        }
      });
    }
  }

  /**
   * Filter a step by application with disabled state (for initial load)
   * Shows all options but disables incompatible ones
   * @param {HTMLElement} stepFieldset - The fieldset to filter
   * @param {string} application - The paint application to filter by
   * @param {string} stepName - Name for logging
   * @private
   */
  #filterStepByApplicationWithDisabled(stepFieldset, application, stepName) {
    
    const stepOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
    
    stepOptions.forEach((option, index) => {
      const optionApplication = /** @type {HTMLElement} */ (option).dataset.application;
      
      // Parse the application data
      let applications = [];
      if (optionApplication) {
        try {
          // First try to parse as JSON array
          const parsed = JSON.parse(optionApplication);
          if (Array.isArray(parsed)) {
            applications = parsed;
          } else {
            applications = [parsed];
          }
        } catch (e) {
          // If JSON parsing fails, treat as comma-separated string
          applications = optionApplication.split(',').map(app => app.trim()).filter(app => app !== '');
        }
      }
      
      const matchesApplication = applications.includes(application);

      const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
      const ariaDisabled = input?.getAttribute('aria-disabled');
      const dataAvailable = input?.getAttribute('data-option-available');
      if (!option.dataset.originalAvailable) {
        option.dataset.originalAvailable = dataAvailable || 'true';
      }
      const originallyUnavailable = ariaDisabled === 'true' || dataAvailable === 'false';

      /* Zelfde set als Liquid first paint: incompatibel blijft verborgen.
         Eerder werden alle opties eerst getoond (disabled) — dat is CLS. */
      if (!matchesApplication) {
        option.classList.add('variant-option--filtered-out');
        option.classList.add('variant-option--hidden-by-liquid');
        if (input) {
          input.disabled = true;
        }
        return;
      }

      option.classList.remove('variant-option--filtered-out');
      option.classList.remove('variant-option--hidden-by-liquid');
      option.style.display = '';

      if (input) {
        const shouldDisable = false;
        
        // Always update disabled state, even if it was previously set
        // This ensures disabled states are correctly updated when paint filter changes
        input.disabled = shouldDisable;
        
        // Store original availability state if not already stored
        if (!option.dataset.originalAvailable) {
          option.dataset.originalAvailable = input.getAttribute('data-option-available') || 'true';
        }
        
        // Handle disabled state for incompatible options
        if (shouldDisable) {
          // Disable incompatible options
          input.disabled = true;
          input.setAttribute('aria-disabled', 'true');
          input.setAttribute('data-option-available', 'false');
          
          // Only add tooltip for step 1 and 2 (not step 3, which uses progressive filtering)
          // Check if this is step 3 or beyond by checking the stepName parameter
          const isStep3OrBeyond = stepName && (stepName.includes('Step 3') || stepName.includes('step 3'));
          
          if (!isStep3OrBeyond) {
            // Add tooltip explaining why this option is disabled
            const variantName = input.value || option.textContent?.trim() || 'deze optie';
            let tooltipText;
            
            // Check if this is step 2 (Inhoud) - should refer to step 1 (Afwerking)
            const isStep2 = stepName && (stepName.includes('Step 2') || stepName.includes('step 2') || stepName.includes('Inhoud'));
            
            if (isStep2) {
              // For step 2, refer to step 1 (previous step)
              const variantSteps = this.querySelectorAll('fieldset.variant-option');
              const step1Fieldset = variantSteps[0]; // Step 1 is index 0
              const step1Legend = step1Fieldset?.querySelector('legend');
              const step1Name = step1Legend?.textContent?.trim() || 'de vorige stap';
              tooltipText = `${variantName} is niet beschikbaar door de huidige selectie van ${step1Name.toLowerCase()}.`;
            } else {
              // For step 1, refer to paint filter
              const paintFilterTitle = this.#getPaintFilterTitle().toLowerCase();
              tooltipText = `${variantName} is niet beschikbaar door de huidige selectie van ${paintFilterTitle}.`;
            }
            
            // Remove title attribute (we'll use custom tooltip)
            option.removeAttribute('title');
            input.removeAttribute('title');
            
            // Create or update tooltip element
            // Place tooltip in fieldset (parent) to avoid overflow clipping from label
            const fieldset = option.closest('fieldset.variant-option');
            if (!option.id) {
              option.id = `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            }
            let tooltip = option.querySelector('.variant-option__tooltip') || (fieldset ? fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${option.id}"]`) : null);
            if (!tooltip) {
              tooltip = document.createElement('div');
              tooltip.className = 'variant-option__tooltip';
              tooltip.setAttribute('role', 'tooltip');
              // Store reference to option for positioning
              tooltip.dataset.optionRef = option.id;
              // Place in fieldset if available, otherwise in document.body
              if (fieldset) {
                fieldset.appendChild(tooltip);
              } else {
                document.body.appendChild(tooltip);
              }
            }
            tooltip.textContent = tooltipText;
            
            // Ensure tooltip is hidden initially
            tooltip.classList.remove('variant-option__tooltip--visible');
            
            // Add event listeners for showing/hiding tooltip
            this.#setupTooltipEvents(/** @type {HTMLElement} */ (option), tooltip);
            
          }
          
          // Add strikethrough SVG if not already present
          if (!option.querySelector('.variant-option__strikethrough')) {
            const strikethrough = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            strikethrough.setAttribute('viewBox', '0 0 100 46');
            strikethrough.setAttribute('preserveAspectRatio', 'xMidYMid slice');
            strikethrough.classList.add('variant-option__strikethrough');
            
            const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line1.setAttribute('x1', '100');
            line1.setAttribute('y1', '0');
            line1.setAttribute('x2', '0');
            line1.setAttribute('y2', '46');
            line1.setAttribute('vector-effect', 'non-scaling-stroke');
            
            const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line2.setAttribute('x1', '100');
            line2.setAttribute('y1', '0');
            line2.setAttribute('x2', '0');
            line2.setAttribute('y2', '46');
            line2.setAttribute('vector-effect', 'non-scaling-stroke');
            
            strikethrough.appendChild(line1);
            strikethrough.appendChild(line2);
            option.appendChild(strikethrough);
          }
        } else {
          // Remove tooltip if option is enabled
          // Tooltip might be in option or in fieldset
          const fieldset = option.closest('fieldset.variant-option');
          const optionId = option.id;
          let tooltip = option.querySelector('.variant-option__tooltip');
          if (!tooltip && fieldset && optionId) {
            tooltip = fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${optionId}"]`);
          }
          if (tooltip) {
            tooltip.remove();
          }
          // Remove event listeners
          if (option._tooltipShowHandler) {
            option.removeEventListener('mouseenter', option._tooltipShowHandler);
            option.removeEventListener('mouseleave', option._tooltipHideHandler);
            delete option._tooltipShowHandler;
            delete option._tooltipHideHandler;
          }
          // Enable compatible options
          input.disabled = false;
          
          // Restore original availability state
          const originalAvailable = option.dataset.originalAvailable || 'true';
          input.setAttribute('data-option-available', originalAvailable);
          
          // Remove aria-disabled if it was set by us (but preserve if originally unavailable)
          if (originalAvailable === 'true') {
            input.removeAttribute('aria-disabled');
          } else {
            input.setAttribute('aria-disabled', 'true');
          }
          
          // Remove strikethrough if it was added by us (but preserve if originally unavailable)
          const strikethrough = option.querySelector('.variant-option__strikethrough');
          if (strikethrough && originalAvailable === 'true') {
            strikethrough.remove();
          }
        }
        
      }
    });

    this.#updateFieldsetColumnWidth(stepFieldset);
  }

  /**
   * Filter a step by application without auto-selection (for after user interaction)
   * Hides incompatible options
   * @param {HTMLElement} stepFieldset - The fieldset to filter
   * @param {string} application - The paint application to filter by
   * @param {string} stepName - Name for logging
   */
  #filterStepByApplicationWithoutAutoSelect(stepFieldset, application, stepName) {
    
    const stepOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
    
    /** @type {HTMLElement[]} */
    let visibleOptions = [];
    stepOptions.forEach((option, index) => {
      const optionApplication = /** @type {HTMLElement} */ (option).dataset.application;
      
      // Parse the application data
      let applications = [];
      if (optionApplication) {
        try {
          // First try to parse as JSON array
          const parsed = JSON.parse(optionApplication);
          if (Array.isArray(parsed)) {
            applications = parsed;
          } else {
            applications = [parsed];
          }
        } catch (e) {
          // If JSON parsing fails, treat as comma-separated string
          applications = optionApplication.split(',').map(app => app.trim()).filter(app => app !== '');
        }
      }
      
      const matchesApplication = applications.includes(application);
      
      // Check availability (only to preserve original state, not for filtering)
      const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
      const ariaDisabled = input?.getAttribute('aria-disabled');
      const dataAvailable = input?.getAttribute('data-option-available');
      // Store original availability if not already stored
      if (!option.dataset.originalAvailable) {
        option.dataset.originalAvailable = dataAvailable || 'true';
      }
      const originallyUnavailable = ariaDisabled === 'true' || dataAvailable === 'false';
      
      // Filter ONLY based on application compatibility, not availability
      // Options that match the application should be visible, even if unavailable
      const isVisible = matchesApplication;
      
      const value = input?.value || 'unknown';
      
      // Update visibility - hide ONLY incompatible options (not unavailable ones)
      if (isVisible) {
        option.classList.remove('variant-option--filtered-out');
        option.classList.remove('variant-option--hidden-by-liquid');
        option.style.display = '';
        visibleOptions.push(/** @type {HTMLElement} */ (option));
        
        // Handle input state
        if (input) {
          // Restore original availability attributes
          const originalAvailable = option.dataset.originalAvailable || 'true';
          input.setAttribute('data-option-available', originalAvailable);
          
          if (originalAvailable === 'true') {
            // Enable if compatible and originally available
            input.disabled = false;
            input.removeAttribute('aria-disabled');
            // Remove strikethrough if it was added by us
            const strikethrough = option.querySelector('.variant-option__strikethrough');
            if (strikethrough) {
              strikethrough.remove();
            }
            // Remove tooltip if it exists (was added for paint filter incompatibility)
            const fieldset = option.closest('fieldset.variant-option');
            const optionId = option.id;
            let tooltip = option.querySelector('.variant-option__tooltip');
            if (!tooltip && fieldset && optionId) {
              tooltip = fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${optionId}"]`);
            }
            if (tooltip) {
              tooltip.remove();
            }
            // Remove event listeners
            if (option._tooltipShowHandler) {
              option.removeEventListener('mouseenter', option._tooltipShowHandler);
              option.removeEventListener('mouseleave', option._tooltipHideHandler);
              delete option._tooltipShowHandler;
              delete option._tooltipHideHandler;
            }
          } else {
            // Keep original unavailable state - disabled with disabled styling
            input.disabled = true;
            input.setAttribute('aria-disabled', 'true');
            // Ensure strikethrough is present for originally unavailable options
            if (!option.querySelector('.variant-option__strikethrough')) {
              const strikethrough = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              strikethrough.setAttribute('viewBox', '0 0 100 46');
              strikethrough.setAttribute('preserveAspectRatio', 'xMidYMid slice');
              strikethrough.classList.add('variant-option__strikethrough');
              
              const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
              line1.setAttribute('x1', '100');
              line1.setAttribute('y1', '0');
              line1.setAttribute('x2', '0');
              line1.setAttribute('y2', '46');
              line1.setAttribute('vector-effect', 'non-scaling-stroke');
              
              const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
              line2.setAttribute('x1', '100');
              line2.setAttribute('y1', '0');
              line2.setAttribute('x2', '0');
              line2.setAttribute('y2', '46');
              line2.setAttribute('vector-effect', 'non-scaling-stroke');
              
              strikethrough.appendChild(line1);
              strikethrough.appendChild(line2);
              option.appendChild(strikethrough);
            }
            // Add tooltip for originally unavailable options explaining they're not available
            // Only add tooltip if it doesn't already exist (to avoid duplicates)
            const fieldset = option.closest('fieldset.variant-option');
            const optionId = option.id || `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            if (!option.id) {
              option.id = optionId;
            }
            let tooltip = option.querySelector('.variant-option__tooltip');
            if (!tooltip && fieldset) {
              tooltip = fieldset.querySelector(`.variant-option__tooltip[data-option-ref="${optionId}"]`);
            }
            
            // Get the name of the previous step dynamically
            const variantSteps = this.querySelectorAll('fieldset.variant-option');
            let previousStepName = 'de vorige stap';
            if (variantSteps.length > 1) {
              const currentStepIndex = Array.from(variantSteps).indexOf(stepFieldset);
              if (currentStepIndex > 0) {
                const previousStepFieldset = variantSteps[currentStepIndex - 1];
                const previousStepLegend = previousStepFieldset.querySelector('legend');
                if (previousStepLegend) {
                  previousStepName = previousStepLegend.textContent?.trim() || 'de vorige stap';
                }
              }
            }
            
            const variantName = input.value || option.textContent?.trim() || 'deze optie';
            const tooltipText = `${variantName} is niet beschikbaar door de huidige selectie van ${previousStepName.toLowerCase()}.`;
            
            if (!tooltip) {
              tooltip = document.createElement('div');
              tooltip.className = 'variant-option__tooltip';
              tooltip.setAttribute('role', 'tooltip');
              tooltip.dataset.optionRef = optionId;
              
              // Place in fieldset if available, otherwise in document.body
              if (fieldset) {
                fieldset.appendChild(tooltip);
              } else {
                document.body.appendChild(tooltip);
              }
              
              // Ensure tooltip is hidden initially
              tooltip.classList.remove('variant-option__tooltip--visible');
              
              // Add event listeners for showing/hiding tooltip
              this.#setupTooltipEvents(/** @type {HTMLElement} */ (option), tooltip);
              
            }
            
            // Update tooltip text (in case it already existed)
            tooltip.textContent = tooltipText;
          }
        }
      } else {
        // Hide incompatible options
        option.classList.add('variant-option--filtered-out');
        option.classList.add('variant-option--hidden-by-liquid');
      }
    });
    

    this.#updateFieldsetColumnWidth(stepFieldset);
  }

  /**
   * Auto-select steps sequentially after filtering is complete
   * @param {NodeList} variantSteps - All variant step fieldsets
   * @param {string} application - The paint application
   */
  #autoSelectStepsSequentially(variantSteps, application) {
    if (!this.#allowAutoSelection) {
      return;
    }
    
    
    // Disable reapply paint filter during auto-selection to prevent loops
    this.#isAutoSelecting = true;
    
    // Process each step immediately (no timeouts needed since filtering is complete)
    for (let currentStepIndex = 0; currentStepIndex < variantSteps.length; currentStepIndex++) {
      const stepFieldset = variantSteps[currentStepIndex];
      const stepName = `Step ${currentStepIndex + 1}`;
      
      
      // Check if current selection is still visible
      const currentSelected = stepFieldset.querySelector('input:checked, option:checked');
      const isCurrentSelectionVisible = currentSelected && 
        !currentSelected.closest('.variant-option__button-label')?.classList.contains('variant-option--hidden-by-liquid') &&
        !currentSelected.classList.contains('variant-option--hidden-by-liquid');
      
      if (isCurrentSelectionVisible) {
        continue;
      }
      
      // Find first visible AND enabled option in DOM order
      const allOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
      let firstVisibleEnabledInDOM = null;
      let visibleEnabledOptionsInDOM = [];
      
      
      for (let i = 0; i < allOptions.length; i++) {
        const option = allOptions[i];
        const isHidden = option.classList.contains('variant-option--hidden-by-liquid') || 
                        option.classList.contains('variant-option--filtered-out') ||
                        option.classList.contains('variant-option--waiting-for-previous-step');
        const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
        const isDisabled = input?.disabled === true;
        const value = input?.value || 'unknown';
        
        
        // Only consider options that are visible AND enabled (not disabled)
        if (!isHidden && !isDisabled) {
          visibleEnabledOptionsInDOM.push({ option, value, index: i });
          if (firstVisibleEnabledInDOM === null) {
            firstVisibleEnabledInDOM = option;
          }
        }
      }
      
      
      if (firstVisibleEnabledInDOM) {
        const input = firstVisibleEnabledInDOM.querySelector('input[type="radio"]') || firstVisibleEnabledInDOM.querySelector('option');
        
        if (input) {
          input.checked = true;
          
          // Trigger change event to update the variant
          const changeEvent = new Event('change', { bubbles: true });
          input.dispatchEvent(changeEvent);
        }
      }
    }
    
    // Re-enable reapply paint filter after auto-selection is done
    this.#isAutoSelecting = false;
  }

  /**
   * Filter variants by application - DISABLED: Show all variants always
   * @param {string} application
   * @param {boolean} shouldClearSelections - Whether to clear selections (only when paint filter changes)
   */
  #filterVariantsByApplication(application, shouldClearSelections = false) {
    if (!application) {
      return;
    }

    // Filter variants based on the selected paint application
    this.#filterStepByApplicationOnly(application, shouldClearSelections);

    // Update visual state for steps after afwerking
    this.#updateStepsAfterAfwerking();
  }

  /**
   * Filter a step based on paint application (legacy method - kept for compatibility)
   * @param {HTMLElement} stepFieldset - The fieldset to filter
   * @param {string} application - The paint application to filter by
   * @param {string} stepName - Name for logging
   */
  #filterStepByApplication(stepFieldset, application, stepName) {
    
    const stepOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
    /** @type {HTMLElement[]} */
    let visibleOptions = [];
    /** @type {HTMLElement[]} */
    let allOptions = [];
    
    stepOptions.forEach((option, index) => {
      allOptions.push(/** @type {HTMLElement} */ (option));
      const optionApplication = /** @type {HTMLElement} */ (option).dataset.application;
      
      
      // Parse the application data
      let applications = [];
      if (optionApplication) {
        try {
          // First try to parse as JSON array
          const parsed = JSON.parse(optionApplication);
          if (Array.isArray(parsed)) {
            applications = parsed;
          } else {
            applications = [parsed];
          }
        } catch (e) {
          // If JSON parsing fails, treat as comma-separated string
          applications = optionApplication.split(',').map(app => app.trim()).filter(app => app !== '');
        }
      }
      
      
      // Check if the selected application matches any of the variant's applications
      const matchesApplication = !application || applications.includes(application);
      
      
      // Check if variant is available
      const input = option.querySelector('input');
      const ariaDisabled = input?.getAttribute('aria-disabled');
      const dataAvailable = input?.getAttribute('data-option-available');
      const isAvailable = ariaDisabled !== 'true' && dataAvailable !== 'false';
      
      const isVisible = matchesApplication && isAvailable;
      
      // Update visibility
      option.classList.toggle('variant-option--filtered-out', !isVisible);
      if (isVisible) {
        option.classList.remove('variant-option--hidden-by-liquid');
        visibleOptions.push(/** @type {HTMLElement} */ (option));
      } else {
        option.classList.add('variant-option--hidden-by-liquid');
      }
    });
    
    // Auto-select first visible option
    this.#autoSelectFirstVisibleVariantInStep(visibleOptions, stepFieldset);

    this.#updateFieldsetColumnWidth(stepFieldset);
  }

  /**
   * Filter a step based on the previous step's selection
   * @param {HTMLElement} stepFieldset - The fieldset to filter
   * @param {string} previousSelection - The selected value from the previous step
   * @param {number} stepNumber - Step number for logging
   */
  #filterStepByPreviousSelection(stepFieldset, previousSelection, stepNumber) {
    
    const stepOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
    /** @type {HTMLElement[]} */
    let visibleOptions = [];
    
    stepOptions.forEach((option, index) => {
      // Get the variant data for this option
      const input = option.querySelector('input');
      const variantId = input?.dataset.variantId;
      
      // Check if this variant is compatible with the previous selection
      const isCompatible = this.#isVariantCompatibleWithSelection(variantId || '', previousSelection, stepNumber - 1);
      
      // Check if variant is available
      const ariaDisabled = input?.getAttribute('aria-disabled');
      const dataAvailable = input?.getAttribute('data-option-available');
      const isAvailable = ariaDisabled !== 'true' && dataAvailable !== 'false';
      
      const isVisible = isCompatible && isAvailable;
      
      // Update visibility
      option.classList.toggle('variant-option--filtered-out', !isVisible);
      if (isVisible) {
        option.classList.remove('variant-option--hidden-by-liquid');
        visibleOptions.push(/** @type {HTMLElement} */ (option));
      } else {
        option.classList.add('variant-option--hidden-by-liquid');
      }
    });
    
    // Auto-select first visible option
    this.#autoSelectFirstVisibleVariantInStep(visibleOptions, stepFieldset);

    this.#updateFieldsetColumnWidth(stepFieldset);
  }

  /**
   * Get the selected value from a step
   * @param {HTMLElement} stepFieldset - The fieldset to get selection from
   * @returns {string|null} - The selected value or null
   */
  #getSelectedValueFromStep(stepFieldset) {
    const selectedInput = stepFieldset.querySelector('input:checked');
    if (selectedInput) {
      return /** @type {HTMLInputElement} */ (selectedInput).value;
    }
    
    const selectedOption = stepFieldset.querySelector('option:checked');
    if (selectedOption) {
      return /** @type {HTMLOptionElement} */ (selectedOption).value;
    }
    
    return null;
  }

  /**
   * Check if a variant is compatible with a previous step's selection
   * @param {string} variantId - The variant ID to check
   * @param {string} previousSelection - The previous step's selection
   * @param {number} previousStepNumber - The previous step number
   * @returns {boolean} - Whether the variant is compatible
   */
  #isVariantCompatibleWithSelection(variantId, previousSelection, previousStepNumber) {
    if (!variantId || !previousSelection) {
      return true; // Show all if no selection
    }
    
    
    // Find all variants that have the previous selection
    const variantsWithPreviousSelection = this.querySelectorAll(`[data-variant-id="${variantId}"]`);
    
    // Check if any of these variants have the previous selection as their option value
    for (const variantElement of variantsWithPreviousSelection) {
      const input = variantElement.closest('label')?.querySelector('input') || variantElement;
      const optionValue = /** @type {HTMLInputElement} */ (input)?.value;
      
      if (optionValue === previousSelection) {
        return true;
      }
    }
    
    // Also check if this variant exists in the same product and has the previous selection
    // This is a more comprehensive check
    const allVariantsWithPreviousSelection = this.querySelectorAll(`input[value="${previousSelection}"], option[value="${previousSelection}"]`);
    
    for (const element of allVariantsWithPreviousSelection) {
      const elementVariantId = /** @type {HTMLElement} */ (element).dataset.variantId;
      if (elementVariantId === variantId) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Show all variants
   */
  #showAllVariants() {
    const variantOptions = this.querySelectorAll('.variant-option__button-label, .variant-option__select option');
    
    requestAnimationFrame(() => {
      variantOptions.forEach(option => {
        option.classList.remove('variant-option--filtered-out');
        option.classList.remove('variant-option--hidden-by-liquid');
      });
      this.#updateAllFieldsetColumnWidths();
    });
  }

  /**
   * Auto-select first visible variant in a specific step
   * @param {HTMLElement[]} visibleOptions - Array of visible variant options
   * @param {HTMLElement} stepFieldset - The fieldset of the step to auto-select in
   */
  #autoSelectFirstVisibleVariantInStep(visibleOptions, stepFieldset) {
    if (!this.#allowAutoSelection) {
      return;
    }
    
    
    if (visibleOptions.length === 0) {
      return;
    }
    
    // Check if current selection is still visible
    const currentSelected = stepFieldset.querySelector('input:checked, option:checked');
    const isCurrentSelectionVisible = currentSelected && 
      !currentSelected.closest('.variant-option__button-label')?.classList.contains('variant-option--hidden-by-liquid') &&
      !currentSelected.classList.contains('variant-option--hidden-by-liquid');
    
    if (isCurrentSelectionVisible) {
      return;
    }
    
    // Find the first visible AND enabled option in DOM order (not in visibleOptions array order)
    const allOptions = stepFieldset.querySelectorAll('.variant-option__button-label');
    let firstVisibleEnabledInDOM = null;
    
    for (const option of allOptions) {
      const isHidden = option.classList.contains('variant-option--hidden-by-liquid') || 
                      option.classList.contains('variant-option--filtered-out') ||
                      option.classList.contains('variant-option--waiting-for-previous-step');
      const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
      const isDisabled = input?.disabled === true;
      
      // Only consider options that are visible AND enabled (not disabled)
      if (!isHidden && !isDisabled) {
        firstVisibleEnabledInDOM = option;
        break;
      }
    }
    
    // Fallback: if no visible enabled option found in DOM order, check the visibleOptions array
    let optionToSelect = firstVisibleEnabledInDOM;
    if (!optionToSelect && visibleOptions.length > 0) {
      // Find first enabled option in the visibleOptions array
      for (const option of visibleOptions) {
        const input = option.querySelector('input[type="radio"]') || option.querySelector('option');
        const isDisabled = input?.disabled === true;
        if (!isDisabled) {
          optionToSelect = option;
          break;
        }
      }
    }
    
    const input = optionToSelect?.querySelector('input[type="radio"]') || optionToSelect?.querySelector('option');
    
    if (input) {
      input.checked = true;
      
      // Trigger change event to update the variant
      const changeEvent = new Event('change', { bubbles: true });
      input.dispatchEvent(changeEvent);
    }
  }

  /**
   * Auto-select first visible variant if current selection is hidden - DISABLED
   * @param {HTMLElement[]} visibleOptions - Array of visible variant options
   */
  #autoSelectFirstVisibleVariant(visibleOptions) {
    // Do nothing - no auto-selection
  }

  /**
   * Reapply current paint filter after variant change
   * For progressive filtering, we only need to ensure the first step is filtered
   * and all other steps remain visible
   * After user interaction with paint filter, disabled options should be hidden instead of just disabled
   */
  #reapplyPaintFilter() {
    // Skip reapply during auto-selection to prevent loops
    if (this.#isAutoSelecting) {
      return Promise.resolve();
    }
    
    
    if (this.#currentPaintFilter) {
      
      // After user interaction, we should hide disabled options instead of just disabling them
      // So we temporarily set #allowAutoSelection to false if user has interacted
      const originalAllowAutoSelection = this.#allowAutoSelection;
      
      this.#filterVariantsByApplication(this.#currentPaintFilter);
      
      // Restore original state (though it shouldn't have changed)
      this.#allowAutoSelection = originalAllowAutoSelection;
    } else {
      this.#showAllVariants();
    }
    
    // Return a promise that resolves when DOM updates are complete
    // This allows callers to wait for disabled states to be updated
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
  }

  /**
   * Get the paint filter title from the paint-application-filter component
   * @returns {string} The paint filter title (default: 'Geschikte ruimte')
   * @private
   */
  #getPaintFilterTitle() {
    const paintFilter = document.querySelector('paint-application-filter');
    if (paintFilter) {
      const legend = paintFilter.querySelector('fieldset legend');
      if (legend) {
        const title = legend.textContent?.trim();
        if (title) {
          return title;
        }
      }
    }
    return 'Geschikte ruimte';
  }

  /**
   * Setup tooltip event listeners for showing/hiding tooltip on hover
   * @param {HTMLElement} option - The option label element
   * @param {HTMLElement} tooltip - The tooltip element
   * @private
   */
  #setupTooltipEvents(option, tooltip) {
    const optionEl = /** @type {HTMLElement} */ (option);
    // Remove existing handlers if any
    if (optionEl._tooltipShowHandler) {
      optionEl.removeEventListener('mouseenter', optionEl._tooltipShowHandler);
      optionEl.removeEventListener('mouseleave', optionEl._tooltipHideHandler);
    }
    
    // Show tooltip on mouse enter
    optionEl._tooltipShowHandler = () => {
      tooltip.classList.add('variant-option__tooltip--visible');
      this.#positionTooltip(optionEl, tooltip);
    };
    
    // Hide tooltip on mouse leave
    optionEl._tooltipHideHandler = () => {
      tooltip.classList.remove('variant-option__tooltip--visible');
    };
    
    optionEl.addEventListener('mouseenter', optionEl._tooltipShowHandler);
    optionEl.addEventListener('mouseleave', optionEl._tooltipHideHandler);
  }

  /**
   * Position tooltip relative to the option element
   * Using fixed positioning to avoid overflow clipping
   * @param {HTMLElement} option - The option label element
   * @param {HTMLElement} tooltip - The tooltip element
   * @private
   */
  #positionTooltip(option, tooltip) {
    // Tooltip uses fixed positioning to avoid overflow clipping from parent
    const optionRect = option.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Position above the element, centered
    let top = optionRect.top - tooltipRect.height - 8;
    let left = optionRect.left + (optionRect.width / 2) - (tooltipRect.width / 2);
    
    // Adjust if tooltip goes off screen
    if (left < 8) {
      left = 8;
    } else if (left + tooltipRect.width > viewportWidth - 8) {
      left = viewportWidth - tooltipRect.width - 8;
    }
    
    // If tooltip doesn't fit above, position below
    if (top < 8) {
      top = optionRect.bottom + 8;
    }
    
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }

  /**
   * Shows the hovered swatch color name in the legend (PDP).
   * @param {Event} event
   */
  previewSwatchColor(event) {
    const label = event.target instanceof Element ? event.target.closest('.variant-option__button-label--has-swatch') : null;
    if (!(label instanceof HTMLElement)) return;

    const fieldset = label.closest('fieldset.variant-option--swatches');
    const swatchValue = fieldset?.querySelector('.variant-option__swatch-value');
    const input = label.querySelector('input[type="radio"]');
    if (!swatchValue || !(input instanceof HTMLInputElement)) return;

    swatchValue.textContent = input.getAttribute('aria-label') || input.value;
  }

  /**
   * Restores the selected swatch color name after hover (PDP).
   * @param {Event} event
   */
  resetSwatchColor(event) {
    const container =
      event.currentTarget instanceof Element && event.currentTarget.classList.contains('variant-picker-swatch-overflow')
        ? event.currentTarget
        : event.target instanceof Element
          ? event.target.closest('.variant-picker-swatch-overflow')
          : null;
    if (!(container instanceof HTMLElement)) return;

    const fieldset = container.closest('fieldset.variant-option--swatches');
    if (!(fieldset instanceof HTMLElement)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && container.contains(relatedTarget)) return;

    this.#updateSwatchValueDisplay(fieldset);
  }

  /**
   * Sync legend swatch label with the selected (or given) option.
   * @param {HTMLElement} fieldset
   * @param {HTMLElement} [selectedOption]
   */
  #updateSwatchValueDisplay(fieldset, selectedOption) {
    const swatchValue = fieldset.querySelector('.variant-option__swatch-value');
    if (!(swatchValue instanceof HTMLElement)) return;

    const option =
      selectedOption ??
      fieldset.querySelector('input[type="radio"]:checked') ??
      fieldset.querySelector('input[type="radio"][data-current-checked="true"]');

    if (!(option instanceof HTMLInputElement)) {
      swatchValue.textContent = swatchValue.dataset.selectedSwatchValue || '';
      return;
    }

    const colorName = option.getAttribute('aria-label') || option.value;
    swatchValue.textContent = colorName;
    swatchValue.dataset.selectedSwatchValue = colorName;
  }

  /**
   * Shows all color swatches on the PDP (+N overflow).
   * @param {Event} [event] - The event that triggered the action.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const overflowList =
      event?.target instanceof Element ? event.target.closest('overflow-list') : null;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('variant-picker')) {
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
}
